import { useCompanySetting } from "Components/CompanySetting/hooks/useCompanySetting";
import { CalendarType } from "Components/Navigation/NavigationHelper";
import moment from "moment";
import { create } from "zustand";
import { getStartOfWeek } from "../helpers/getStartOfWeek";
import { getCompanyWorkingMinutes } from "Components/WorkingHours/helpers/getCompanyWorkingMinutes";
import { MinuteRange } from "Components/WorkingHours/types/MinuteRange";
import { devLog } from "utils/developmentLogger";

interface State {
	weekPlus: number;
	calendarType: CalendarType;
	startOfWeek: moment.Moment;
	selectedDate: moment.Moment;
	workingMinutes: MinuteRange;
	setWeekPlus: (weekPlus: number) => void;
	setCalendarType: (calendarType: CalendarType) => void;
	setSelectedDate: (date: moment.Moment) => void;
	refreshStartOfWeek: () => void;
	refreshWorkingMinutes: () => void;
	initializeWithState: (initialDate: moment.Moment, initialCalendarType: CalendarType) => void;
	updateWithState: (initialDate: moment.Moment, initialCalendarType: CalendarType) => void;
}

// This will be called after initialization is complete
let initialStateSet = false;

export const useCalendar = create<State>((set, get) => {
	// Start with safe defaults
	const defaultWeekPlus = 0;
	const defaultCalendarType = CalendarType.WEEK;
	const defaultSelectedDate = moment().startOf("day");
	
	return {
		weekPlus: defaultWeekPlus,
		calendarType: defaultCalendarType,
		startOfWeek: getStartOfWeek(defaultWeekPlus),
		selectedDate: defaultSelectedDate,
		workingMinutes: getCompanyWorkingMinutes(),
		setWeekPlus: (weekPlus) => {
			set({ weekPlus });
			get().refreshStartOfWeek();
		},
		setCalendarType: (calendarType) => {
			set({ calendarType });
			// Note: No longer saves to localStorage - view selection is based on settings or attributes only
		},
		setSelectedDate: (date) => set({ selectedDate: date.clone().startOf("day") }),
		refreshStartOfWeek: () => {
			const weekPlus = get().weekPlus;
			const companySetting = useCompanySetting.getState().item;
			const startOfWeek = getStartOfWeek(weekPlus, companySetting);

			set({ startOfWeek });
		},
		refreshWorkingMinutes: () => {
			const companySetting = useCompanySetting.getState().item;
			const workingMinutes = getCompanyWorkingMinutes(companySetting);

			set({ workingMinutes });
		},
		initializeWithState: (initialDate, initialCalendarType) => {
			// Only initialize once
			if (initialStateSet) {
				// If already initialized, use updateWithState instead
				get().updateWithState(initialDate, initialCalendarType);
				return;
			}
			initialStateSet = true;
			
			get().updateWithState(initialDate, initialCalendarType);
		},
		updateWithState: (initialDate, initialCalendarType) => {
			devLog.log("[useCalendar] Updating calendar state with:", { 
				date: initialDate.format('YYYY-MM-DD'), 
				type: CalendarType[initialCalendarType] 
			});
			
			// Calculate weekPlus based on the initial date
			let weekPlus = 0;
			if (initialCalendarType === CalendarType.WEEK || initialCalendarType === CalendarType.MONTH) {
				const currentWeek = moment().startOf('week');
				const selectedWeek = initialDate.clone().startOf('week');
				weekPlus = selectedWeek.diff(currentWeek, 'weeks');
				devLog.log("[useCalendar] Calculated weekPlus for week/month view:", { 
					currentWeek: currentWeek.format('YYYY-MM-DD'), 
					selectedWeek: selectedWeek.format('YYYY-MM-DD'), 
					weekPlus 
				});
			} else if (initialCalendarType === CalendarType.DAY) {
				// For day view, we still need to calculate weekPlus to ensure the navigation shows the correct week
				const currentWeek = moment().startOf('week');
				const selectedWeek = initialDate.clone().startOf('week');
				weekPlus = selectedWeek.diff(currentWeek, 'weeks');
				devLog.log("[useCalendar] Calculated weekPlus for day view:", { 
					currentWeek: currentWeek.format('YYYY-MM-DD'), 
					selectedWeek: selectedWeek.format('YYYY-MM-DD'), 
					weekPlus 
				});
			}
			
			devLog.log("[useCalendar] Setting state:", {
				selectedDate: initialDate.format('YYYY-MM-DD'),
				calendarType: CalendarType[initialCalendarType],
				weekPlus
			});
			
			set({
				selectedDate: initialDate.clone().startOf('day'),
				calendarType: initialCalendarType,
				weekPlus,
			});
			
			get().refreshStartOfWeek();
			
			devLog.log("[useCalendar] State updated, new startOfWeek:", get().startOfWeek.format('YYYY-MM-DD'));
		},
	};
});
